import { useState } from "react";
import { Link2, Facebook, Instagram, Linkedin, Mail, Share2 } from "lucide-react";

type ShareButtonsProps = {
  url: string;
  title: string;
  /** 4:5 (1080x1350) card used for Instagram, which crops other ratios. */
  instagramImage?: string;
  /** UI language for the labels; the share targets are identical. */
  lang?: "en" | "es";
};

/** Visible and assistive labels per language. */
const LABELS = {
  en: {
    section: "Share this post",
    facebook: "Share on Facebook; on mobile, choose Facebook from the share menu",
    linkedin: "Share on LinkedIn",
    email: "Share via email",
    instagram: "Download the Instagram image for this post (1080 by 1350)",
    instagramTitle: "Download the 4:5 Instagram image",
    native: "Share via device share menu",
    copy: "Copy link to this post",
    copyLabel: "Copy link",
    copied: "Copied!",
  },
  es: {
    section: "Compartir esta publicación",
    facebook: "Compartir en Facebook; en el celular, elija Facebook en el menú de compartir",
    linkedin: "Compartir en LinkedIn",
    email: "Compartir por correo electrónico",
    instagram: "Descargar la imagen de Instagram de esta publicación (1080 por 1350)",
    instagramTitle: "Descargar la imagen 4:5 para Instagram",
    native: "Compartir con el menú del dispositivo",
    copy: "Copiar el enlace de esta publicación",
    copyLabel: "Copiar enlace",
    copied: "¡Copiado!",
  },
} as const;

export function ShareButtons({ url, title, instagramImage, lang = "en" }: ShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const t = LABELS[lang];

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharer/sharer.php?url=${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share({ title, url });
      } catch {
        // User cancelled — no action needed
      }
    } else {
      handleCopy();
    }
  };

  const handleFacebookShare = async (event: React.MouseEvent<HTMLAnchorElement>) => {
    const isMobile = window.matchMedia("(pointer: coarse)").matches;

    if (isMobile && "share" in navigator) {
      event.preventDefault();
      try {
        await navigator.share({ title, url });
      } catch {
        // Cancelling the native share sheet requires no follow-up action.
      }
      return;
    }

    // Desktop: Facebook's sharer only completes reliably in a real popup
    // window. Inside an iframe (preview/embeds) a plain target="_blank" tab
    // inherits a sandboxed context and the dialog hangs on "processing".
    event.preventDefault();
    const width = 626;
    const height = 536;
    const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
    const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);
    const popup = window.open(
      shareLinks.facebook,
      "facebook-share-dialog",
      `popup=yes,width=${width},height=${height},left=${left},top=${top}`,
    );
    if (!popup) {
      // Popup blocked — fall back to a normal tab on the top-level window.
      window.open(shareLinks.facebook, "_blank", "noopener,noreferrer");
    } else {
      popup.focus();
    }
  };

  /** Fetch the card and save it client-side, avoiding an <a href> to a file. */
  const handleImageDownload = async (src: string) => {
    try {
      const res = await fetch(src);
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const tmp = document.createElement("a");
      tmp.href = objectUrl;
      tmp.download = src.split("/").pop() || "instagram-card.png";
      document.body.appendChild(tmp);
      tmp.click();
      document.body.removeChild(tmp);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(src, "_blank", "noopener");
    }
  };


  return (
    <section aria-label={t.section} className="mt-10 border-t pt-6">
      <div className="flex flex-wrap items-center gap-3">
        <span className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Share2 className="h-4 w-4" aria-hidden="true" />
          {t.section}
        </span>

        <div className="flex flex-wrap items-center gap-2">
          {/* Facebook */}
          <a
            href={shareLinks.facebook}
            onClick={handleFacebookShare}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.facebook}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted hover:text-brand-mid"
          >
            <Facebook className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
          </a>

          {/* LinkedIn */}
          <a
            href={shareLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.linkedin}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted hover:text-brand-mid"
          >
            <Linkedin className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
          </a>

          {/* Email */}
          <a
            href={shareLinks.email}
            aria-label={t.email}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted hover:text-brand-mid"
          >
            <Mail className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
          </a>

          {/* Instagram — download the 4:5 portrait card (1080x1350).
              Rendered as a <button>, never an <a href> to an image file:
              crawlers must only follow page links, not resource files. */}
          {instagramImage && (
            <button
              type="button"
              onClick={() => handleImageDownload(instagramImage)}
              aria-label={t.instagram}
              title={t.instagramTitle}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted hover:text-brand-mid"
            >
              <Instagram className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
            </button>
          )}


          {/* Native share (mobile) */}
          <button
            type="button"
            onClick={handleNativeShare}
            aria-label={t.native}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:bg-muted hover:text-brand-mid md:hidden"
          >
            <Share2 className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />
          </button>

          {/* Copy link */}
          <button
            type="button"
            onClick={handleCopy}
            aria-label={t.copy}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-border px-4 text-sm font-medium text-foreground transition-colors hover:bg-muted hover:text-brand-mid"
          >
            <Link2 className="h-4 w-4" aria-hidden="true" />
            {copied ? t.copied : t.copyLabel}
          </button>
        </div>
      </div>
    </section>
  );
}
